import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { MODEL_LABEL, fmtPrice, validDates } from "./priceUtils";

const errMsg = (e, d) => formatApiErrorDetail(e.response?.data?.detail) || d;

export function NewBatchDialog({ rules, canOverride, onCreated, onClose }) {
  const dates = validDates(rules.today, rules.notice_days);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [date, setDate] = useState(dates[0]);
  const [urgent, setUrgent] = useState(false);
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/price-list/batches", { name, message, effective_date: date, override_ack: ack, override_reason: reason });
      toast.success("Draft change batch created");
      onCreated(data);
    } catch (e) { toast.error(errMsg(e, "Could not create the batch")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="price-batch-new-dialog">
        <DialogHeader><DialogTitle>New change batch</DialogTitle>
          <DialogDescription>Changes to existing prices start on the 1st of a month at least {rules.notice_days} days away. Nothing is announced until you approve the batch.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2027 price update" data-testid="price-batch-name" /></div>
          <MessageField value={message} onChange={setMessage} />
          {!urgent ? (
            <div><Label>Effective date</Label>
              <select value={date} onChange={(e) => setDate(e.target.value)} data-testid="price-batch-date"
                      className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{dates.map((d) => <option key={d}>{d}</option>)}</select></div>
          ) : (
            <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3" data-testid="price-batch-override">
              <p className="flex gap-2 text-xs text-red-800"><AlertTriangle className="h-4 w-4 shrink-0" />
                Policy: partners get at least {rules.notice_days} days' notice and changes start on the 1st of a month. An urgent override is recorded in the Audit Log with your reason.</p>
              <Input type="date" value={date} min={rules.today} onChange={(e) => setDate(e.target.value)} data-testid="price-batch-override-date" />
              <label className="flex items-center gap-2 text-xs text-red-800">
                <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="price-batch-override-ack" /> I understand this breaks the {rules.notice_days}-day notice policy
              </label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="price-batch-override-reason" />
            </div>
          )}
          {canOverride && (
            <button type="button" className="text-xs text-red-700 hover:underline" data-testid="price-batch-urgent-toggle"
                    onClick={() => { setUrgent(!urgent); setDate(dates[0]); setAck(false); }}>
              {urgent ? "Back to a standard date" : "Urgent change — earlier or mid-month date (super admin)"}</button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={busy || !date || (urgent && (!ack || reason.trim().length < 5))} data-testid="price-batch-create">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MessageField = ({ value, onChange }) => (
  <div><Label>Message to partners (optional)</Label>
    <Textarea value={value} onChange={(e) => onChange(e.target.value.slice(0, 1000))} rows={3} data-testid="price-batch-message"
              placeholder="Why are prices changing? Shown in the emails, on the price list and in the PDF." />
    <div className="text-right text-[10px] text-slate-400">{value.length}/1000</div></div>
);

function AcksPanel({ batchId }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/price-list/batches/${batchId}/acks`).then(({ data }) => setA(data)).catch(() => setA(null)); }, [batchId]);
  if (!a) return null;
  return (
    <div className="rounded-md border border-slate-200 p-3 text-xs" data-testid="price-review-acks">
      <b>Read receipts:</b> {a.acknowledged} of {a.total} partner(s) confirmed
      <ul className="mt-2 max-h-48 divide-y divide-slate-100 overflow-y-auto">
        {a.partners.map((p) => (
          <li key={p.org_id} className="flex items-center gap-2 py-1">
            <span className="flex-1 truncate">{p.name}</span>
            {p.ack ? <span className="text-emerald-700">Read · {p.ack.user_name} · {p.ack.at.slice(0, 10)}</span>
              : <span className={p.has_admin ? "text-amber-700" : "text-slate-400"}>{p.has_admin ? "Not read yet" : "No Partner Admin"}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReviewDialog({ batch, canApprove, onDone, onClose }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(batch.message || "");
  const saveMessage = async () => {
    try { await api.patch(`/price-list/batches/${batch.batch_id}`, { message }); toast.success("Message saved"); }
    catch (e) { toast.error(errMsg(e, "Could not save the message")); }
  };
  useEffect(() => {
    api.get(`/price-list/batches/${batch.batch_id}/review`).then(({ data }) => setR(data))
      .catch((e) => toast.error(errMsg(e, "Could not load the review")));
  }, [batch.batch_id]);
  const approve = async () => {
    if (!window.confirm(`Approve "${batch.name}" and email ${r.partner_admins} Partner Admin(s) now?`)) return;
    setBusy(true);
    try {
      if (message !== (batch.message || "")) await api.patch(`/price-list/batches/${batch.batch_id}`, { message });
      const { data } = await api.post(`/price-list/batches/${batch.batch_id}/approve`);
      toast.success(`Scheduled — ${data.emails_sent} email(s) sent to ${data.partners} partner(s)`);
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not approve")); } finally { setBusy(false); }
  };
  const blocked = !r || r.placeholders.length > 0 || r.rows.length === 0 || !canApprove || batch.status !== "draft";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="price-review-dialog">
        <DialogHeader><DialogTitle>Review "{batch.name}"</DialogTitle>
          <DialogDescription>Effective {batch.effective_date}{batch.override ? ` · urgent override: ${batch.override.reason}` : ""}</DialogDescription></DialogHeader>
        {!r ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-300" /> : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto text-sm">
            {r.placeholders.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="price-review-placeholders">
                <b>Fix these $9,999 placeholders before approving:</b>
                <ul className="mt-1 list-disc pl-5">{r.placeholders.map((p, i) => <li key={i}>{p.name} — {MODEL_LABEL[p.model]}</li>)}</ul>
              </div>
            )}
            <table className="w-full" data-testid="price-review-rows">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400"><th>Item</th><th>Model</th><th className="text-right">Current</th><th className="text-right">New</th></tr></thead>
              <tbody>{r.rows.map((x, i) => (
                <tr key={i} className="border-t border-slate-100"><td className="py-1">{x.name}</td><td>{MODEL_LABEL[x.model]}</td>
                  <td className="text-right tabular-nums">{x.old === null ? "Price on request" : fmtPrice(x.old)}</td>
                  <td className={`text-right tabular-nums font-medium ${x.direction === "up" ? "text-red-600" : x.direction === "down" ? "text-emerald-600" : ""}`}>
                    {x.removed ? "Removed" : x.new_na ? "N/A" : x.new === null ? "$9,999 placeholder" : fmtPrice(x.new)}</td></tr>))}</tbody>
            </table>
            {batch.status === "draft" ? (
              <div><MessageField value={message} onChange={setMessage} />
                <Button size="sm" variant="outline" onClick={saveMessage} disabled={message === (batch.message || "")} data-testid="price-batch-message-save">Save message</Button></div>
            ) : batch.message && (
              <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-2 text-xs text-amber-900 whitespace-pre-line" data-testid="price-review-message">{batch.message}</div>
            )}
            {batch.status === "scheduled" && <AcksPanel batchId={batch.batch_id} />}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="price-review-recipients">
              <b>{r.partners.length}</b> partner(s) affected · <b>{r.partner_admins}</b> Partner Admin email(s) · <b>{r.staff_emails.length}</b> staff copies.
              Reminder goes out automatically 7 days before the date.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {batch.status === "draft" && <Button onClick={approve} disabled={blocked || busy} data-testid="price-review-approve"
                  title={!canApprove ? "You need the Approve & notify permission" : ""}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve &amp; notify</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ImportDialog({ batches, isSuper, onDone, onClose }) {
  const [p, setP] = useState(null);
  const [now, setNow] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const drafts = batches.filter((b) => b.status === "draft");
  const [batchId, setBatchId] = useState(drafts[0]?.batch_id || "");
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/price-list/import/preview", fd); setP(data); }
    catch (e) { toast.error(errMsg(e, "Could not read the file")); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/price-list/import/apply", { token: p.token, batch_id: p.batch.length && !now ? batchId : null, apply_now: now, reason });
      toast.success(`Imported: ${data.new_items} new (draft), ${data.updated} updated, ${data.batched} live price change(s) ${now ? "applied now" : "added to the batch"}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, "Import failed")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="price-import-dialog">
        <DialogHeader><DialogTitle>Import price list</DialogTitle>
          <DialogDescription>Start from <b>Export CSV</b> (or Excel): keep the Item ID column, fill in the price and note columns (blank price = $9,999 placeholder, "N/A" = not applicable), add new rows with an empty Item ID. You'll see every difference before anything is applied.</DialogDescription></DialogHeader>
        {!p ? (
          <Input type="file" accept=".csv,.xlsx" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="price-import-file" />
        ) : (
          <div className="max-h-[55vh] space-y-3 overflow-y-auto text-sm" data-testid="price-import-preview">
            <p><b>{p.new_items.length}</b> new item(s) (added as drafts) · <b>{p.direct.length}</b> item(s) updated now · <b>{p.batch.length}</b> live price change(s) for a change batch · <b>{p.errors.length}</b> error(s)</p>
            {p.errors.map((e, i) => <p key={i} className="text-xs text-red-600">Row {e.row}: {e.error}</p>)}
            {p.new_items.map((n, i) => <p key={`n${i}`} className="text-xs text-emerald-700">+ {n.name}</p>)}
            {p.direct.map((d, i) => <p key={`d${i}`} className="text-xs text-slate-600">~ {d.name}</p>)}
            {p.batch.flatMap((b) => b.rows).map((x, i) => (
              <p key={`b${i}`} className="text-xs text-amber-700">⏱ {x.name} — {MODEL_LABEL[x.model]}: {x.old === null ? "on request" : fmtPrice(x.old)} → {x.new_na ? "N/A" : x.new === null ? "$9,999" : fmtPrice(x.new)}</p>))}
            {p.batch.length > 0 && isSuper && (
              <div className="space-y-2 rounded-md border border-slate-200 p-2 text-xs" data-testid="price-import-mode">
                <label className="flex items-center gap-2"><input type="radio" checked={!now} onChange={() => setNow(false)} /> Put live price changes into a draft change batch (30-day notice)</label>
                <label className="flex items-center gap-2 text-red-700"><input type="radio" checked={now} onChange={() => setNow(true)} data-testid="price-import-now" /> Apply immediately — urgent, no notice to partners (recorded in the Audit Log)</label>
                {now && <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="price-import-reason" />}
              </div>
            )}
            {p.batch.length > 0 && !now && (
              drafts.length ? (
                <div><Label>Put live price changes into</Label>
                  <select value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="price-import-batch"
                          className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
                    {drafts.map((b) => <option key={b.batch_id} value={b.batch_id}>{`${b.name} (${b.effective_date})`}</option>)}</select></div>
              ) : <p className="text-xs text-red-600">Create a draft change batch first — live price changes need one.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {p && <Button onClick={apply} disabled={busy || (p.batch.length > 0 && (now ? reason.trim().length < 5 : !batchId))} data-testid="price-import-apply">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply import</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
